import { useMutation } from '@tanstack/react-query';
import { Alert, App, Button, Card, Flex, Form, Input, Typography } from 'antd';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../../lib/api-client';
import { useSession } from '../auth/session';

const { Title, Text } = Typography;

interface Values {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * Changing your own password.
 *
 * Every field is an `Input.Password`, which carries antd's show/hide eye — a
 * password nobody can read back is how a typo becomes a lockout, and this is
 * exactly the screen where that costs the most.
 *
 * Changing it revokes every session, including this one, so the person is
 * signed out and comes back with the new password. That is deliberate: if the
 * reason for the change is that somebody else knows the old one, leaving any
 * session alive defeats the point.
 */
export function ChangePasswordPage({ firstSignIn = false }: { firstSignIn?: boolean }): ReactNode {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<Values>();
  const signOut = useSession((s) => s.signOut);
  const [error, setError] = useState<string | null>(null);

  const change = useMutation({
    mutationFn: (values: Values) =>
      api<undefined>('/auth/change-password', {
        method: 'POST',
        body: { currentPassword: values.currentPassword, newPassword: values.newPassword },
        withoutBranch: true,
      }),
    onSuccess: () => {
      message.success(t('iam.passwordChanged'));
      // The server has already revoked this session; clearing local state is
      // what stops the app sitting there holding a token nobody honours.
      void signOut();
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : t('iam.genericError'));
    },
  });

  return (
    <Flex justify="center" style={{ paddingTop: 24 }}>
      <Card style={{ width: '100%', maxWidth: 460 }}>
        <Flex vertical gap={16}>
          <Flex vertical gap={4}>
            <Title level={4} style={{ margin: 0 }}>
              {firstSignIn ? t('iam.firstSignInTitle') : t('iam.changePassword')}
            </Title>
            <Text type="secondary">
              {firstSignIn ? t('iam.firstSignInIntro') : t('iam.changePasswordIntro')}
            </Text>
          </Flex>

          {error !== null && <Alert type="error" showIcon message={error} />}

          <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            onFinish={(values) => {
              setError(null);
              change.mutate(values);
            }}
          >
            <Form.Item
              name="currentPassword"
              label={t('iam.currentPassword')}
              rules={[{ required: true, message: t('iam.enterCurrent') }]}
            >
              <Input.Password size="large" autoFocus autoComplete="current-password" />
            </Form.Item>

            <Form.Item
              name="newPassword"
              label={t('iam.newPassword')}
              dependencies={['currentPassword']}
              rules={[
                { required: true, min: 8, message: t('iam.passwordRule') },
                // The API refuses it too: "changing" a temporary password to
                // itself would leave whoever set it able to sign in as you.
                ({ getFieldValue }) => ({
                  validator(_, value: string) {
                    if (value === '' || value !== getFieldValue('currentPassword')) return Promise.resolve();
                    return Promise.reject(new Error(t('iam.passwordSame')));
                  },
                }),
              ]}
              extra={t('iam.passwordHint')}
            >
              <Input.Password size="large" autoComplete="new-password" />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label={t('iam.confirmPassword')}
              dependencies={['newPassword']}
              rules={[
                { required: true, message: t('iam.confirmRequired') },
                // Checked here rather than server-side: a mismatch is a typo,
                // and the person should see it before anything is sent.
                ({ getFieldValue }) => ({
                  validator(_, value: string) {
                    // Empty is handled by the `required` rule above; this only
                    // has to answer "do the two match".
                    if (value === '' || value === getFieldValue('newPassword')) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t('iam.passwordsDiffer')));
                  },
                }),
              ]}
            >
              <Input.Password size="large" autoComplete="new-password" />
            </Form.Item>

            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('iam.willSignOut')}
            />

            <Button type="primary" htmlType="submit" size="large" block loading={change.isPending}>
              {t('iam.changePassword')}
            </Button>
          </Form>

          {firstSignIn && (
            <Button type="link" onClick={() => void signOut()}>
              {t('iam.signOut')}
            </Button>
          )}
        </Flex>
      </Card>
    </Flex>
  );
}
